import type {ComponentType} from 'react';
import {AiChatProTipType} from './ai-chat-pro-tip-type';
import {ProTipSlotLayout, ProTipVariant} from './ai-prompt-box-pro-tip.types';
import {ChangeStyleAnnouncementVisual} from './components/change-style-announcement-visual/change-style-announcement-visual';
import {EditableTextLayersVisual} from '@Components/editable-text-layers-visual/editable-text-layers-visual';

export interface TextProTipDisplay {
  variant: ProTipVariant.TEXT;
  icon: string;
  messages: string[];
}

interface VisualProTipDisplayBase {
  titleKey: string;
  descriptionKey: string;
  mobileTitleKey?: string;
  mobileDescriptionKey?: string;
}

export interface ImageProTipDisplay extends VisualProTipDisplayBase {
  variant: ProTipVariant.IMAGE;
  imageUrl: string;
}

export interface ComponentProTipDisplay extends VisualProTipDisplayBase {
  variant: ProTipVariant.COMPONENT;
  VisualComponent: ComponentType;
  slotLayout?: ProTipSlotLayout;
}

export type ProTipDisplay = TextProTipDisplay | ImageProTipDisplay | ComponentProTipDisplay;

export interface ProTipConfig {
  type: AiChatProTipType;
  display: ProTipDisplay;
  priority: number;
  triggerKeywords?: string[];
  /**
   * When true, the tip is never suppressed by the "seen" state — it fires
   * whenever its trigger criteria are met, subject only to PRO_TIP_COOLDOWN_MS.
   * Use for recurring problems we always want to nudge the user about (e.g.
   * long-chat context loss, referring to an older version).
   */
  persistent?: boolean;
  markSeenOnDismiss?: boolean;
}

const LOW_RES_THRESHOLD = 256;

export const PRO_TIP_COOLDOWN_MS = 10000;

export const PRO_TIP_CONFIGS: ProTipConfig[] = [
  {
    type: AiChatProTipType.EDITABLE_TEXT_LAYERS_ANNOUNCEMENT,
    display: {
      variant: ProTipVariant.COMPONENT,
      VisualComponent: EditableTextLayersVisual,
      slotLayout: ProTipSlotLayout.PORTRAIT,
      titleKey: 'pmwjs_protip_editable_text_layers_title',
      descriptionKey: 'pmwjs_protip_editable_text_layers_desc',
    },
    priority: 0,
  },
  {
    type: AiChatProTipType.CHANGE_STYLE_ANNOUNCEMENT,
    display: {
      variant: ProTipVariant.COMPONENT,
      VisualComponent: ChangeStyleAnnouncementVisual,
      titleKey: 'pmwjs_protip_change_style_announcement_title',
      descriptionKey: 'pmwjs_protip_change_style_announcement_desc',
      mobileTitleKey: 'pmwjs_protip_change_style_announcement_title_mobile',
      mobileDescriptionKey: 'pmwjs_protip_change_style_announcement_desc_mobile',
    },
    priority: 1,
  },
  {
    type: AiChatProTipType.ATTACHMENT_ADDED,
    display: {
      variant: ProTipVariant.TEXT,
      icon: 'icon-paperclip',
      messages: ['pmwjs_protip_attachment_specify', 'pmwjs_protip_attachment_refer', 'pmwjs_protip_attachment_clear_images'],
    },
    priority: 1,
  },
  {
    type: AiChatProTipType.LOW_RES_ATTACHMENT,
    display: {
      variant: ProTipVariant.TEXT,
      icon: 'icon-hd',
      messages: ['pmwjs_protip_low_res'],
    },
    priority: 2,
  },
  {
    type: AiChatProTipType.REFER_OLDER_VERSION,
    display: {
      variant: ProTipVariant.TEXT,
      icon: 'icon-paperclip',
      messages: ['pmwjs_protip_reattach_older', 'pmwjs_protip_edit_older_messages'],
    },
    priority: 3,
    triggerKeywords: ['go back', 'previous design', 'original design', 'return to'],
    persistent: true,
  },
  {
    type: AiChatProTipType.EXACT_TEXT_PROMPTING,
    display: {
      variant: ProTipVariant.TEXT,
      icon: 'icon-message-prompt',
      messages: ['pmwjs_protip_text_use_quotes', 'pmwjs_protip_text_add_exact'],
    },
    priority: 4,
    triggerKeywords: ['text', 'font', 'title', 'heading', 'headline', 'subtitle', 'caption', 'slogan', 'tagline', 'word', 'letter', 'write', 'typing'],
  },
  {
    type: AiChatProTipType.SPELLING_ISSUES,
    display: {
      variant: ProTipVariant.TEXT,
      icon: 'icon-spellcheck',
      messages: ['pmwjs_protip_spelling'],
    },
    priority: 5,
    triggerKeywords: ['spelling', 'spellings', 'misspelled', 'correct spelling', 'correct spellings', 'spelling error', 'spelling errors'],
  },
  {
    type: AiChatProTipType.LONG_CHAT_CONTEXT,
    display: {
      variant: ProTipVariant.TEXT,
      icon: 'icon-new-chat',
      messages: ['pmwjs_protip_long_chat_new', 'pmwjs_protip_long_chat_fresh'],
    },
    priority: 6,
    persistent: true,
  },
  {
    type: AiChatProTipType.VAGUE_PROMPT,
    display: {
      variant: ProTipVariant.TEXT,
      icon: 'icon-library',
      messages: ['pmwjs_protip_vague_prompt'],
    },
    priority: 7,
  },
  {
    type: AiChatProTipType.TOO_LONG_TEXT,
    display: {
      variant: ProTipVariant.TEXT,
      icon: 'icon-scroll-text',
      messages: ['pmwjs_protip_too_long_text'],
    },
    priority: 8,
  },
  {
    type: AiChatProTipType.EDITING_TIPS,
    display: {
      variant: ProTipVariant.TEXT,
      icon: 'icon-zoom-in',
      messages: ['pmwjs_protip_editing_describe', 'pmwjs_protip_editing_location', 'pmwjs_protip_editing_exact_changes'],
    },
    priority: 9,
  },
];

const PERSISTENT_TIP_TYPES = new Set<AiChatProTipType>(PRO_TIP_CONFIGS.filter((config) => config.persistent === true).map((config) => config.type));

const MARK_SEEN_ON_DISMISS_TIP_TYPES = new Set<AiChatProTipType>(PRO_TIP_CONFIGS.filter((config) => config.markSeenOnDismiss === true).map((config) => config.type));

export function isPersistentTip(type: AiChatProTipType): boolean {
  return PERSISTENT_TIP_TYPES.has(type);
}

export function isMarkSeenOnDismissTip(type: AiChatProTipType): boolean {
  return MARK_SEEN_ON_DISMISS_TIP_TYPES.has(type);
}

export {LOW_RES_THRESHOLD};
